<template>
	<div class="alarm-list" v-loading="loading">
		<div class="section-header">
			<span class="section-title">最新告警</span>
			<el-pagination
				small
				layout="prev, pager, next"
				:current-page="currentPage"
				:page-size="pageSize"
				:total="total"
				@current-change="handlePageChange"
			></el-pagination>
		</div>
		<div class="alarm-scroll">
			<gc-empty v-if="!alarmData.length" class="alarm-empty" />
			<div class="alarm-card" v-for="(item, index) in alarmData" :key="index" :class="item.level">
				<div class="alarm-card-header">
					<span class="alarm-title" :class="item.level">{{ item.title }}</span>
					<span class="severity-badge" :class="item.level">
						<i class="badge-dot"></i>
						{{ levelLabel(item.level) }}
					</span>
				</div>
				<div class="alarm-card-body">
					<div class="alarm-info">
						<span class="info-label">设备:</span>
						<span>{{ item.deviceNo }}</span>
						<span class="info-location" v-if="item.location">({{ item.location }})</span>
					</div>
					<div class="alarm-time">
						<span class="info-label">发生时间:</span>
						<span>{{ item.time }}</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { apiGetPressureRegulatorAlarms } from '@/apis/pressureRegulatorManage.api'

export default {
	name: 'AlarmList',
	props: {
		timeRange: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			loading: false,
			alarmData: [],
			currentPage: 1,
			pageSize: 10,
			total: 0,
		}
	},
	watch: {
		timeRange: {
			handler() {
				this.currentPage = 1
				this.getAlarmList()
			},
			immediate: true,
		},
	},
	methods: {
		getAlarmList() {
			this.loading = true
			apiGetPressureRegulatorAlarms({
				current: this.currentPage,
				size: this.pageSize,
				alarmSeq: 0,
				...this.getTimeParams(),
			})
				.then(res => {
					this.alarmData = (res.records || []).map(this.normalizeAlarm)
					this.total = res.total || 0
				})
				.catch(() => {
					this.alarmData = []
					this.total = 0
				})
				.finally(() => {
					this.loading = false
				})
		},
		normalizeAlarm(item) {
			return {
				...item,
				title: item.alarmName || '--',
				deviceNo: item.deviceNo || item.regulatorCode || '--',
				location: item.address || '',
				time: item.alarmTime || '--',
				level: this.getLevelKey(item.alarmLevel),
			}
		},
		getTimeParams() {
			const [startTime] = this.timeRange || []
			return {
				alarmStartTime: startTime ? this.dayjs(startTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
			}
		},
		handlePageChange(page) {
			this.currentPage = page
			this.getAlarmList()
		},
		getLevelKey(level) {
			const map = {
				0: 'info',
				1: 'warning',
				2: 'urgent',
			}
			return map[level] || 'info'
		},
		levelLabel(level) {
			const map = { urgent: '紧急', warning: '重要', info: '次要' }
			return map[level] || '告警'
		},
	},
}
</script>

<style lang="scss" scoped>
.alarm-list {
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	height: 480px;
}
.section-header {
	height: 54px;
	padding: 0 24px;
	border-bottom: 1px solid var(--line);
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-shrink: 0;
}
.section-title {
	font-size: 17px;
	font-weight: 700;
	color: var(--title);
}
.alarm-scroll {
	height: 0;
	flex: 1;
	overflow-y: scroll;
	padding: 0 12px;
	margin: 12px 0;

	&::-webkit-scrollbar {
		width: 6px;
	}
	&::-webkit-scrollbar-track {
		background: #f5f5f5;
		border-radius: 3px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c1c1c1;
		border-radius: 3px;

		&:hover {
			background: #a8a8a8;
		}
	}
}

.alarm-empty {
	height: 100%;
	margin-top: 0;
	padding-top: 75px;
}

.alarm-card {
	border-radius: 12px;
	border: 1px solid #f0f0f0;
	padding: 14px 16px;
	margin-bottom: 10px;

	&:last-child {
		margin-bottom: 0;
	}

	// urgent - red
	&.urgent {
		background: #fff1f0;
		border-color: #ffccc7;
	}
	// warning/important - orange/yellow
	&.warning {
		background: #fff8e1;
		border-color: #ffe58f;
	}
	// info/minor - pale yellow
	&.info {
		background: #fffde7;
		border-color: #fff59d;
	}
}
.alarm-card-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 10px;
}
.alarm-title {
	font-size: 15px;
	font-weight: 700;
	color: var(--title);

	&.urgent {
		color: #f5222d;
	}
	&.warning {
		color: #fa8c16;
	}
	&.info {
		color: #d48806;
	}
}
.severity-badge {
	display: inline-flex;
	align-items: center;
	font-size: 12px;
	padding: 2px 8px;
	border-radius: 10px;
	flex-shrink: 0;

	.badge-dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		display: inline-block;
		margin-right: 4px;
	}

	&.urgent {
		color: #f5222d;
		background: rgba(245, 34, 45, 0.1);

		.badge-dot {
			background: #f5222d;
		}
	}
	&.warning {
		color: #fa8c16;
		background: rgba(250, 140, 22, 0.1);

		.badge-dot {
			background: #fa8c16;
		}
	}
	&.info {
		color: #d48806;
		background: rgba(212, 136, 6, 0.1);

		.badge-dot {
			background: #d48806;
		}
	}
}
.alarm-card-body {
	.alarm-info,
	.alarm-time {
		font-size: 13px;
		color: var(--muted);
		line-height: 1.8;
	}
	.info-label {
		color: var(--muted);
		margin-right: 4px;
	}
	.info-location {
		margin-left: 2px;
	}
}
</style>
